import React, { createRef } from 'react';
import classNameBind from 'classnames/bind';
import _has from 'lodash/has';
import ActionList from '../../ActionList';
import Spinner from '../../Spinner';
import styles from './WithAutoComplete.module.css';
import { KEYS } from '../../../utils/methods/keyboardKeys';

const bindClasses = classNameBind.bind(styles);

const valueMatchesItem = (value, item) => (
  _has(item, 'value')
  && item.value.toLowerCase().indexOf(value.toLowerCase()) !== -1
  && item.value.length >= value.length
);

const alreadySelected = (item, currentItems) => currentItems.map(i => i.id).indexOf(item.id) !== -1;

const getAvailableOptions = (value, availableItems, currentItems) => {
  const result = [];

  if (availableItems) {
    result.push(...(availableItems.filter(item => valueMatchesItem(value, item))));
  }

  if (Array.isArray(currentItems)) {
    return result.filter(item => (!alreadySelected(item, currentItems)));
  }

  return result;
};

const groupByGroupId = (options, groups) => {
  const result = [
    { id: undefined, name: '', items: [] },
    ...groups.map(group => ({ ...group, items: [] })),
  ];

  options.forEach((option) => {
    result.find(group => group.id === option.groupId).items.push({
      ...option,
      name: option.value,
    });
  });

  return result.filter(group => group.items.length > 0);
};

const getAvailableOptionsOrderedByGroup = (value, availableItems, currentItems, groups) => {
  const availableOptions = getAvailableOptions(value, availableItems, currentItems);
  const grouped = groupByGroupId(availableOptions, groups);

  return grouped.reduce((acc, current) => [...acc, ...current.items], []);
};

export default () => Component => (
  class WithAutoComplete extends React.Component {
    inputRef = createRef();

    wrapperRef = createRef();

    static defaultProps = {
      isLoading: false,
      groups: [],
    };

    state = {
      open: false,
      selected: null,
      inputValue: '',
    }

    onItemSelected = (item) => {
      const {
        name,
        onChange,
        onNextChange,
        value,
        options,
        groups,
      } = this.props;
      const { inputValue } = this.state;
      const currentValue = [...value, {
        id: item.id,
        value: item.value,
      }];
      const items = getAvailableOptionsOrderedByGroup(inputValue, options, currentValue, groups);

      onChange({
        target: {
          name,
          value: currentValue,
        },
      });
      this.inputRef.current.clear();
      this.inputRef.current.focus();
      if (items.length > 0) {
        this.setState({
          selected: items[0],
          inputValue: '',
        });
      } else {
        this.setState({
          inputValue: '',
        });
      }
      if (onNextChange) {
        onNextChange({
          target: {
            name: `${name}[${currentValue.length}]`,
            value: '',
          },
        });
      }
    };

    handleFocus = () => {
      const { inputValue } = this.state;
      const {
        disabled,
        value,
        options,
        onFocus,
        groups,
      } = this.props;
      const items = getAvailableOptionsOrderedByGroup(inputValue, options, value, groups);

      if (disabled) return;

      if (onFocus) onFocus();

      this.setState({
        open: true,
        selected: items[0],
      });
    }

    handleChange = (event) => {
      const { onChange } = this.props;

      onChange(event);
      this.setState({
        open: true,
      });
    }

    handleNextChange = (event) => {
      const { inputValue } = this.state;
      const { options, value, onNextChange, groups } = this.props;
      const filteredItems = getAvailableOptionsOrderedByGroup(inputValue, options, value, groups);

      this.setState({
        inputValue: event.target.value,
        selected: filteredItems[0],
      });
      if (onNextChange) {
        onNextChange(event);
      }
    }

    handlePressEnter = (event) => {
      const { open, selected, inputValue } = this.state;
      const { options, value, groups } = this.props;
      const filteredItems = getAvailableOptionsOrderedByGroup(inputValue, options, value, groups);

      if (open) {
        event.preventDefault();
      }

      if (selected && filteredItems.length > 0) {
        this.onItemSelected(selected);
      }
    }

    handleKeyDown = (event) => {
      const { options, value, groups } = this.props;
      const { selected } = this.state;
      const items = getAvailableOptionsOrderedByGroup(event.target.value, options, value, groups);

      if (items.length > 0 && [KEYS.UP, KEYS.DOWN].includes(event.key)) {
        const selectedId = (selected || options[0]).id;
        const selectedIndex = items.findIndex(item => (
          item.id === selectedId
        ));
        event.preventDefault();

        if (event.key === KEYS.DOWN) {
          if (selected === null) {
            this.setState({
              open: true,
              selected: items[0],
            });

            return;
          }

          if (selectedIndex !== items.length - 1) {
            this.setState({
              open: true,
              selected: items[selectedIndex + 1],
            });
          }
        } else if (event.key === KEYS.UP && selectedIndex > 0) {
          this.setState({
            selected: items[selectedIndex - 1],
          });
        } else if (event.key === KEYS.UP && selectedIndex === 0) {
          this.setState({
            open: false,
            selected: null,
          });
        }
      }
    }

    handleAutoCompleteBlur = (event) => {
      const {
        onBlur,
        name,
        value,
        disabled,
      } = this.props;

      if (!this.wrapperRef.current.contains(event.relatedTarget) && !disabled) {
        this.setState({
          open: false,
        });
        if (onBlur) {
          onBlur({
            target: {
              name,
              value,
            },
          });
        }
      }
    }

    render() {
      const { open, selected, inputValue } = this.state;
      const {
        options,
        value,
        t,
        placeholder,
        isLoading,
      } = this.props;
      const filteredItems = getAvailableOptions(inputValue, options, value);
      const current = selected ? selected.id : null;

      return (
        <div
          className={bindClasses('wrapper')}
          ref={this.wrapperRef}
          onBlur={this.handleAutoCompleteBlur}
        >
          <Component
            {...this.props}
            canAddMore={false}
            ref={this.inputRef}
            onFocus={this.handleFocus}
            onChange={this.handleChange}
            onPressEnter={this.handlePressEnter}
            onNextChange={this.handleNextChange}
            editable={false}
            onKeyDown={options.length > 0 ? this.handleKeyDown : undefined}
            placeholder={placeholder || t('TagsInput.WithAutoComplete.placeholder')}
          />
          {open && filteredItems.length > 0 && (
            <div className={bindClasses('list')} style={{ width: this.wrapperRef.current.offsetWidth }}>
              {isLoading ? (
                <Spinner />
              ) : (
                <ActionList
                  groups={groupByGroupId(filteredItems, this.props.groups)}
                  onItemSelected={this.onItemSelected}
                  current={current}
                />
              )}
            </div>
          )}
        </div>
      );
    }
  }
);
